import { ApiRequestError } from "./client.js";

/** Anmeldezustand, wie ihn der Server meldet. Enthaelt nichts Geheimes. */
export interface AuthState {
  authenticated: boolean;
  level: "oidc_authenticated" | "vault_totp_verified" | null;
  displayName: string | null;
  isOwner: boolean;
  /** Ob Authentik auf diesem Server ueberhaupt eingerichtet ist. */
  vaultConfigured: boolean;
  vaultExpiresAt: string | null;
}

async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method,
    headers: body === undefined ? {} : { "content-type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? "unknown_error",
      error?.message ?? "Unerwarteter Fehler.",
    );
  }
  return payload as T;
}

export const authApi = {
  state: () => json<AuthState>("GET", "/api/auth/state"),

  /**
   * Beginnt die Anmeldung.
   *
   * Der Server liefert die Adresse von Authentik; die Navigation dorthin macht
   * der Browser. Eine Weiterleitung im Hintergrund waere hier falsch -- der
   * Nutzer soll sehen, wohin er geht.
   */
  beginLogin: (redirectTo?: string) =>
    json<{ redirectUrl: string }>(
      "POST",
      "/api/auth/login",
      redirectTo ? { redirectTo } : {},
    ),

  logout: () => json<void>("POST", "/api/auth/logout"),

  /** Prueft den zweiten Faktor und liefert den neuen Anmeldezustand. */
  verifyTotp: (code: string) => json<AuthState>("POST", "/api/auth/totp", { code }),
};

/** Verstaendliche Meldungen zu den Fehlercodes, die der Rueckruf mitgibt. */
export const LOGIN_ERROR_TEXT: Record<string, string> = {
  not_owner: "Dieses Konto hat keinen Zugriff auf den geschützten Bereich.",
  unknown_state: "Der Anmeldevorgang ist abgelaufen. Bitte erneut versuchen.",
  login_failed: "Die Anmeldung konnte nicht überprüft werden.",
  missing_state: "Die Anmeldung ist unvollständig zurückgekommen.",
  access_denied: "Die Anmeldung wurde abgebrochen.",
};

export function describeLoginError(code: string): string {
  return LOGIN_ERROR_TEXT[code] ?? "Die Anmeldung ist fehlgeschlagen.";
}
